import { ReactNode, createContext, useContext, useMemo, useRef } from "react";
import { useSyncExternalStoreWithSelector } from "use-sync-external-store/shim/with-selector";
import { deepEqual } from "fast-equals";
import {
  buildListingStateFromInitial,
  listingStore,
  type ListingStoreState,
} from "src/stores/listing/listing.store";
import type { SerializedListingResult } from "src/types/product-listing";

const FALLBACK_SNAPSHOT = buildListingStateFromInitial(undefined);

const ListingServerSnapshotContext =
  createContext<ListingStoreState>(FALLBACK_SNAPSHOT);

export function ListingStoreProvider({
  initialListingState,
  children,
}: {
  initialListingState?: SerializedListingResult;
  children: ReactNode;
}) {
  const serverSnapshot = useMemo(
    () => buildListingStateFromInitial(initialListingState),
    [initialListingState]
  );

  const seededRef = useRef(false);

  if (
    typeof window !== "undefined" &&
    !seededRef.current &&
    initialListingState?.products
  ) {
    listingStore.setState({
      sortOptions: initialListingState.sortOptions,
    });
    seededRef.current = true;
  }

  return (
    <ListingServerSnapshotContext.Provider value={serverSnapshot}>
      {children}
    </ListingServerSnapshotContext.Provider>
  );
}

export function useListingServerSnapshot(): ListingStoreState {
  return useContext(ListingServerSnapshotContext);
}

export function useListingStore<T>(
  selector: (state: ListingStoreState) => T,
  isEqual: (a: T, b: T) => boolean = Object.is
): T {
  const serverSnapshot = useContext(ListingServerSnapshotContext);

  const getServerSnapshot = useMemo(
    () => () => serverSnapshot,
    [serverSnapshot]
  );

  return useSyncExternalStoreWithSelector(
    listingStore.subscribe,
    listingStore.getSnapshot,
    getServerSnapshot,
    selector,
    isEqual
  );
}

const selectProducts = (state: ListingStoreState) => state.products;
const selectFacets = (state: ListingStoreState) => state.facets;
const selectTree = (state: ListingStoreState) => state.tree;
const selectAppliedFacets = (state: ListingStoreState) => state.appliedFacets;
const selectPage = (state: ListingStoreState) => state.page;
const selectSort = (state: ListingStoreState) => state.sort;
const selectSortOption = (state: ListingStoreState) => state.sortOption;
const selectSortOptions = (state: ListingStoreState) => state.sortOptions;
const selectTotalPages = (state: ListingStoreState) => state.totalPages;
const selectTotalResults = (state: ListingStoreState) => state.totalResults;
const selectResultId = (state: ListingStoreState) => state.resultId;
const selectLoading = (state: ListingStoreState) => state.loading;
const selectHasFetched = (state: ListingStoreState) => state.hasFetched;
const selectRequest = (state: ListingStoreState) => state.request;

export function useListingState() {
  const productsByPage = useListingStore(selectProducts, deepEqual);
  const facets = useListingStore(selectFacets, deepEqual);
  const tree = useListingStore(selectTree, deepEqual);
  const appliedFacets = useListingStore(selectAppliedFacets, deepEqual);
  const page = useListingStore(selectPage);
  const sort = useListingStore(selectSort);
  const sortOption = useListingStore(selectSortOption, deepEqual);
  const sortOptions = useListingStore(selectSortOptions, deepEqual);
  const totalPages = useListingStore(selectTotalPages);
  const totalResults = useListingStore(selectTotalResults);
  const resultId = useListingStore(selectResultId);
  const loading = useListingStore(selectLoading);
  const hasFetched = useListingStore(selectHasFetched);
  const request = useListingStore(selectRequest, deepEqual);

  return {
    productsByPage,
    facets,
    tree,
    appliedFacets,
    page,
    sort,
    sortOption,
    sortOptions,
    totalPages,
    totalResults,
    resultId,
    loading,
    hasFetched,
    request,
  };
}
